import { useDispatch } from "react-redux";
import { get_and_search_jobs } from "../features/Jobseeker/jobseekerSlice";
import AppSecureStorage from "../services/storage/secureStorage";

const storage = new AppSecureStorage();

export const unauthorizedError = (navigate) => {
  navigate("/");
  storage.clearStorage();
};

// helper.js

export function formatDOBDate(dateString) {
  const date = new Date(dateString);

  const options = {
    day: '2-digit',
    month: 'short',  // This will give abbreviated month names, like "Aug"
    year: 'numeric'
  };

  return date.toLocaleDateString('en-GB', options);
}

// dateHelper.js
export const getYear = (dateString) => {
  if (!dateString) return ""; // Handle cases where the input is null or undefined
  const date = new Date(dateString);
  return isNaN(date.getFullYear()) ? "" : date.getFullYear(); // Ensure valid year
};

// Function to get the full date in YYYY-MM-DD format
export const getDate = (dateString) => {
  if (!dateString) return ""; // Handle cases where the input is null or undefined

  const date = new Date(dateString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return ""; // Return an empty string if the date is invalid
  }

  // Extract and return the date in YYYY-MM-DD format
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based, so we add 1 and pad with 0 if needed
  const day = String(date.getDate()).padStart(2, '0'); // Pad the day with a leading zero if it's a single digit

  return `${year}-${month}-${day}`; // Return the formatted date
};

export const timeSincePosted = (createdAt) => {
  if (!createdAt) {
    // If there's no date, return an empty string or a default message
    return '';
  }

  const createdDate = new Date(createdAt);

  // Check if the date is invalid
  if (isNaN(createdDate)) {
    return '';
  }

  const now = new Date();
  const diffInMs = now.getTime() - createdDate.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays <= 0) return 'Posted today';
  if (diffInDays === 1) return 'Posted 1 day ago';
  return `Posted ${diffInDays} days ago`;
};

export const formatDateTime = (isoString) => {
  const dateObj = new Date(isoString);

  // Extract date components
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(dateObj.getDate()).padStart(2, '0');

  // Extract time components
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  const seconds = String(dateObj.getSeconds()).padStart(2, '0');

  // Combine into desired format
  const formattedDate = `${day}-${month}-${year}`;
  const formattedTime = `${hours}:${minutes}:${seconds}`;

  return { date: formattedDate, time: formattedTime };
};

export const cleanDescription = (rawHTML) => {
  if (!rawHTML) return '';
  return rawHTML
    .replace(/<li>\s*<br>\s*<\/li>/g, '')        
    .replace(/<li><br><\/li>/g, '')             
    .replace(/<li>\s*<\/li>/g, '')       
    .replace(/<p><br><\/p>/g, '')            
    .replace(/<br\s*\/?>\s*/g, '<br />')
    .replace(/<p>(\s|<br\s*\/?>)*<\/p>/gi, '')     
    .trim();
};


export const getLightColorForTag = (index) => {
  const hue = (index * 110) % 280;
  const saturation = 90;
  const lightness = 90 + (index % 20);
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export const getRemainingDaysMessage = (expirationDateTime) => {
  if (!expirationDateTime) return '';

  const today = new Date();
  const expiry = new Date(expirationDateTime);
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return 'Expired';
  if (diffDays === 1) return 'Expires in 1 day';
  if (diffDays <= 5) return `Only ${diffDays} days left!`; // Urgency message
  return `Expires in ${diffDays} days`; // Normal message
};


export const parseSearchParams = (searchParams) => {
  const params = new URLSearchParams(searchParams);
  const parsedParams = {};
  params.forEach((value, key) => {
    parsedParams[key] = value;
  });
  return parsedParams;
};

export const onSearchJobs = (onSearchValues, dispatch, navigate, apiCall, userId = null) => {
  const filteredValues = Object.keys(onSearchValues)?.reduce((acc, key) => {
    const value = onSearchValues[key];
    if (value && typeof value === "string" && value.trim() !== "") {
      acc[key] = value.trim(); // Include valid string values
    }
    return acc;
  }, {});

  // Convert filtered search parameters to query string
  const searchParams = new URLSearchParams(filteredValues).toString();

  // Dispatch the API call dynamically, with or without userId
  if (apiCall && typeof apiCall === "function") {
    if (userId) {
      // API requires userId
      dispatch(apiCall({ navigate, userId, searchParams }));
    } else {
      // API does not require userId
      dispatch(apiCall({ navigate, searchParams }));
    }
  } 
};


export const applyFiltersAllPages = (internalSearchParameter, searchParameters, onSearchValues, filters, dispatch, navigate, apiCall, userId = null) => {
  // Helper function to parse existing search parameters into an object
  const parseSearchParams = (paramString) => {
    const params = new URLSearchParams(paramString);
    const result = {};
    for (let [key, value] of params.entries()) {
      result[key] = value;
    }
    return result;
  };

  // Parse existing search parameters if available
  const parsedInternalParams = internalSearchParameter
    ? parseSearchParams(internalSearchParameter)
    : {};
  const parsedSearchParams = searchParameters
    ? parseSearchParams(searchParameters)
    : {};
  const parsedonSearchParams = onSearchValues
    ? parseSearchParams(onSearchValues)
    : {};

  // Filter out unnecessary fields with values of 0
  const filteredFilters = Object.entries(filters)?.reduce((acc, [key, value]) => {
    if (value !== 0 && value !== "0") { // Exclude fields with value 0 or "0"
      acc[key] = value;
    }
    return acc;
  }, {});

  // Merge parsed parameters with current filters
  const combinedFilters = {
    ...parsedInternalParams,
    ...parsedSearchParams,
    ...parsedonSearchParams,
    ...filteredFilters,
  };

  // Create the final search string
  const searchParams = new URLSearchParams(combinedFilters).toString();

  // Dispatch API call with the filtered parameters
  if (apiCall && typeof apiCall === "function") {
    if (userId) {
      // API requires userId
      dispatch(apiCall({ navigate, userId, searchParams }));
    } else {
      // API does not require userId
      dispatch(apiCall({ navigate, searchParams }));
    }
  }
};

export const withoutClickapplyFiltersAllPages = (internalSearchParameter, searchParameters, onSearchValues, filters, dispatch, navigate, apiCall, userId = null) => {
  // Helper function to parse existing search parameters into an object
  const parseSearchParams = (paramString) => {
    const params = new URLSearchParams(paramString);
    const result = {};
    for (let [key, value] of params.entries()) {
      result[key] = value;
    }
    return result;
  };

  // Parse existing search parameters if available
  const parsedInternalParams = internalSearchParameter
    ? parseSearchParams(internalSearchParameter)
    : {};
  const parsedSearchParams = searchParameters
    ? parseSearchParams(searchParameters)
    : {};
  const parsedonSearchParams = onSearchValues
    ? parseSearchParams(onSearchValues)
    : {};

  // Filter out unnecessary fields with values of 0
  // const filteredFilters = Object.entries(filters)?.reduce((acc, [key, value]) => {
  //   if (value !== 0 && value !== "0") { // Exclude fields with value 0 or "0"
  //     acc[key] = value;
  //   }
  //   return acc;
  // }, {});

  const filteredFilters = Object.entries(filters)?.reduce((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});

  // Merge parsed parameters with current filters
  const combinedFilters = {
    ...parsedInternalParams,
    ...parsedSearchParams,
    ...parsedonSearchParams,
    ...filteredFilters,
  };

  // Create the final search string
  const searchParams = new URLSearchParams(combinedFilters).toString();

  // Dispatch API call with the filtered parameters
  if (apiCall && typeof apiCall === "function") {
    if (userId) {
      // API requires userId
      dispatch(apiCall({ navigate, userId, searchParams }));
    } else {
      // API does not require userId
      dispatch(apiCall({ navigate, searchParams }));
    }
  } else {
    console.error("Invalid API call function provided!");
  }
};


export const clientId = () => {
  return process.env.GOOGLE_CLIENT_ID
}

export const formatDescription = (desc, iswhite = false) => {
  if (!desc) return <p>Description : NA</p>;

  const lines = desc.split("\n");
  const formattedElements = [];
  let bulletList = [];

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    // Skip empty lines
    if (!trimmedLine) return;

    // Detect headings (Standalone lines that are likely followed by content)
    const isHeading =
      /^[A-Za-z\s?]+$/.test(trimmedLine) &&
      (lines[index + 1]?.trim() === "" || /^[A-Za-z]/.test(lines[index + 1]?.trim()));

    if (isHeading) {
      // Push previous bullet list if exists
      if (bulletList.length) {
        formattedElements.push(<ul key={`ul-${index}`}>{bulletList}</ul>);
        bulletList = [];
      }
      formattedElements.push(
        <p className={`font-w-700 ${iswhite ? 'text-white' : 'text-black'}`} key={index}>{trimmedLine}</p>
      );
      return;
    }

    // Detect bullet points and store them in a list
    if (/^[-•*]/.test(trimmedLine) || /^[A-Za-z]/.test(trimmedLine)) {
      bulletList.push(
        <li className={`font-w-400 ${iswhite ? 'text-white' : 'text-black'}`} key={index}>{trimmedLine}</li>
      );
      return;
    }

    // Push previous bullet list before adding a new paragraph
    if (bulletList.length) {
      formattedElements.push(<ul key={`ul-${index}`}>{bulletList}</ul>);
      bulletList = [];
    }

    // Default: Normal paragraph text
    formattedElements.push(
      <p className={`font-w-400 ${iswhite ? 'text-white' : 'text-black'}`} key={index}>{trimmedLine}</p>
    );
  });

  // Push remaining bullet list at the end
  if (bulletList.length) {
    formattedElements.push(<ul key="final-list">{bulletList}</ul>);
  }

  return formattedElements;
};

export const onOrgSearchJobs = (onSearchValues, dispatch, navigate, apiCall, userId = null, status) => {
  const filteredValues = Object.keys(onSearchValues)?.reduce((acc, key) => {
    const value = onSearchValues[key];
    if (value && typeof value === "string" && value.trim() !== "") {
      acc.push(`${key}=${encodeURIComponent(value.trim())}`);
    }
    return acc;
  }, []);

  // Manually create the searchParams string
  const searchParams = filteredValues.join('&');

  if (apiCall && typeof apiCall === "function") {
    if (userId) {
      dispatch(apiCall({ navigate, userId, searchParams, status }));
    } else {
      dispatch(apiCall({ navigate, searchParams, status }));
    }
  } else {
    console.error("Invalid API call function provided!");
  }
};

